import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import type { AssetFile, ManualMediaRef } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import type { AuthSession } from "../../../../auth/keycloak.js";
import { AssetLibraryDrawer } from "../../asset-library/AssetLibraryDrawer.js";
import { borderFor, canvasFromImage, composite, frame, loadImageCors, type Direction } from "./collage.js";
import { boundedForExport } from "../image-export.js";

/** The image extensions the collage picker accepts (only rasters composite cleanly onto a `<canvas>`). */
const IMAGE_TYPES = [".jpg", ".jpeg", ".png", ".webp", ".gif"];

/**
 * In-house image **collage** editor (ADR-025 A3b) — legacy `ARLMS_ModalWindows.js` "Zusätzliche Bilder" parity WITHOUT the
 * server-side FFmpeg. The step image is the starting base; a **"+" on the right edge** appends the next image horizontally and
 * a **"+" on the bottom edge** appends it vertically (a black gap between), each fold wrapping the whole running collage
 * ({@link composite}). All compositing happens on an off-screen `<canvas>` in NATURAL pixels (like the annotate editor — a
 * `fetch(mode:"cors")` blob keeps the export untainted); the picked image comes from the shared {@link AssetLibraryDrawer}
 * (pick mode). Save applies the black OUTER {@link frame} once, exports a PNG (`<name>_collage.png`) and hands it to the
 * caller's `onSave` (the same edit-attach write as A3a); on success we call `onSaved` (the caller re-opens the lightbox on the
 * new image). Cancel / X / backdrop call `onClose`.
 */
export function ImageCollageEditor({
  media,
  session,
  onClose,
  onSaved,
  onSave,
  manualId = null,
}: {
  media: ManualMediaRef;
  session: AuthSession | null;
  /** Dismiss without saving (Cancel / X / backdrop) — returns to wherever the editor was opened from. */
  onClose: () => void;
  /** A successful save landed — the caller shows the collaged result (the lightbox) rather than the bare manual. */
  onSaved: () => void;
  onSave: (file: File) => Promise<unknown>;
  /** The manual in context (#217 P2) — enables the picker's "This manual" project-files scope. */
  manualId?: number | null;
}) {
  const { t } = useTranslation();
  // The RUNNING collage canvas (natural pixels). `previewUrl` is its data-URL, shown scaled-to-fit in the stage.
  const collageRef = useRef<HTMLCanvasElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Which edge the pending pick appends to (opens the asset drawer); null ⇒ no picker open.
  const [picking, setPicking] = useState<Direction | null>(null);
  // Count of images appended so far — Save needs ≥1 (a "collage" of just the base image is a no-op).
  const [appended, setAppended] = useState(0);
  const [busy, setBusy] = useState(false); // compositing a freshly-picked image
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the step image as the starting base — the SAME untainted-export load as the annotate editor: a plain <img> for this
  // presigned URL could reuse a cached OPAQUE response and taint the export canvas, so fetch `mode:"cors"` → same-origin blob.
  // A missing bucket CORS rule fails the fetch → the same remediation message + everything disabled.
  useEffect(() => {
    if (!media.url) return;
    let cancelled = false;
    loadImageCors(media.url)
      .then((img) => {
        if (cancelled) return;
        const canvas = canvasFromImage(img);
        collageRef.current = canvas;
        setPreviewUrl(canvas.toDataURL("image/png"));
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [media.url]);

  // The picked library image is composited onto the running collage in the chosen direction, and the new canvas replaces it.
  const onPick = (file: AssetFile) => {
    const dir = picking;
    const base = collageRef.current;
    // Close the drawer IMMEDIATELY (panel F3): the compositing load is async, and while the drawer stayed open a SECOND
    // "Use" click would branch off the SAME un-updated base canvas → last-write-wins silently drops an appended image. The
    // `busy` guard is belt-and-suspenders against a re-entrant pick.
    setPicking(null);
    if (busy || !file.rawUrl || !dir || !base) return;
    setBusy(true);
    setError(null);
    loadImageCors(file.rawUrl)
      .then((img) => {
        const next = composite(base, img, dir, borderFor(base.width, base.height));
        collageRef.current = next;
        setPreviewUrl(next.toDataURL("image/png"));
        setAppended((n) => n + 1);
      })
      .catch(() => setError(t("manualEditor.imageAddFailed")))
      .finally(() => setBusy(false));
  };

  const save = () => {
    const base = collageRef.current;
    if (!base) return;
    setSaving(true);
    setError(null);
    // Apply the black OUTER frame ONCE at export (folds never accumulate frames — see collage.ts), bound the resolution so a
    // multi-image collage can't blow the upload byte cap / server decode limit, then export a PNG.
    boundedForExport(frame(base, borderFor(base.width, base.height))).toBlob((blob) => {
      if (!blob) {
        setSaving(false);
        setError(t("manualEditor.collageFailed"));
        return;
      }
      const name = (media.name ?? "image").replace(/\.[^./\\]+$/, "");
      const file = new File([blob], `${name}_collage.png`, { type: "image/png" });
      // Hand the PNG to the caller (the same edit-attach write as A3a) — on success signal `onSaved` (the caller re-opens the
      // lightbox on the collaged image); on error keep the editor open with the message so the author can retry.
      onSave(file)
        .then(() => onSaved())
        .catch((e: unknown) => setError(e instanceof Error ? e.message : t("manualEditor.saveFailed")))
        .finally(() => setSaving(false));
    }, "image/png");
  };

  const disabled = saving || busy || !ready || loadError;

  // No presigned URL (storage unconfigured) → nothing to composite onto; don't render the editor at all.
  if (!media.url) return null;

  return (
    <>
      <ModalOverlay isDismissable isOpen onOpenChange={(o) => !o && onClose()} className="fixed inset-0 z-[70] flex flex-col bg-black/80 p-4">
        <Modal className="flex min-h-0 w-full flex-1 flex-col outline-hidden">
          <Dialog aria-label={t("manualEditor.combineImages", { name: media.name ?? t("manualEditor.imageFallback") })} className="flex min-h-0 flex-1 flex-col outline-none">
            {/* Header */}
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="truncate text-sm font-medium text-white">{t("manualEditor.addMoreImagesFull", { name: media.name ?? t("manualEditor.imageFallback") })}</span>
              <Button variant="ghost" size="icon-lg" aria-label={t("manualEditor.closeEditor")} onPress={onClose} className="text-white hover:bg-white/15 hover:text-white">
                <X aria-hidden="true" />
              </Button>
            </div>

            {/* Toolbar */}
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-white/10 p-2">
              <span className="text-xs text-white/80">{t("manualEditor.addImagesHint")}</span>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="ghost" size="sm" onPress={onClose} className="text-white hover:bg-white/15 hover:text-white">
                  {t("manualEditor.cancel")}
                </Button>
                <Button variant="default" size="sm" onPress={save} isDisabled={disabled || appended === 0}>
                  {saving ? <Loader2 aria-hidden="true" className="mr-1.5 size-3.5 animate-spin" /> : null}
                  {saving ? t("manualEditor.saving") : t("manualEditor.saveAsNewImage")}
                </Button>
              </div>
            </div>

            {/* Status line */}
            {error ? (
              <p role="alert" className="mb-2 rounded-md bg-red-500/20 px-3 py-1.5 text-xs text-red-100">
                {error}
              </p>
            ) : null}

            {/* Collage stage. The image is centered + scaled to fit; the two "+" affordances are anchored to the STAGE
                edges (right-centre + bottom-centre) via `absolute` + `z-10`, NOT to the image. Image-anchored "+" broke
                because a wide image gets height-capped in this wide full-screen stage → the bottom "+" fell at/below the
                viewport edge and was unreachable until a right-append made the image shorter. Stage-anchored, both are always
                on-screen + clickable, sitting in the padding gap so they still hug the image. */}
            <div className="relative flex min-h-0 flex-1 items-center justify-center p-10 sm:p-14">
              {loadError ? (
                <p className="max-w-md text-center text-sm text-white/80">
                  {t("manualEditor.imageLoadCorsFailed")}
                </p>
              ) : previewUrl ? (
                <img src={previewUrl} alt={`Collage preview for ${media.name ?? "image"}`} className="block max-h-full max-w-full object-contain" />
              ) : null}
              {previewUrl && !loadError ? (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={t("manualEditor.addImageRight")}
                    isDisabled={disabled}
                    onPress={() => setPicking("right")}
                    className="absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-full shadow-lg sm:right-4"
                  >
                    <Plus aria-hidden="true" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={t("manualEditor.addImageBelow")}
                    isDisabled={disabled}
                    onPress={() => setPicking("bottom")}
                    className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2 rounded-full shadow-lg sm:bottom-4"
                  >
                    <Plus aria-hidden="true" />
                  </Button>
                </>
              ) : null}
              {(!ready || busy) && !loadError ? <Loader2 aria-hidden="true" className="absolute size-8 animate-spin text-white/70" /> : null}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>

      {/* Asset picker (ADR-024) — a react-aria Drawer, so it STACKS over the editor modal and manages its own focus. Pick mode
          restricted to raster images; on select we composite in the pending direction. Opened per-"+" (the direction is `picking`). */}
      {picking ? (
        <AssetLibraryDrawer session={session} isOpen mode="pick" manualId={manualId} types={IMAGE_TYPES} onClose={() => setPicking(null)} onSelect={onPick} overlayClassName="z-[80]" />
      ) : null}
    </>
  );
}
